/** Shared date/time formatting for the dashboard. */

const TZ = "America/Sao_Paulo";

/** `yyyy-mm-dd` (input/picker) → `yyyy-M-dd` (Fiti API). */
export function toApiDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${y}-${parseInt(m, 10)}-${String(parseInt(d, 10)).padStart(2, "0")}`;
}

export function todayIso(): string {
  return new Date().toLocaleDateString("sv-SE", { timeZone: TZ });
}

export function shiftIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

function isoToDate(iso: string): Date | null {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isSunday(iso: string): boolean {
  const date = isoToDate(iso);
  return date !== null && date.getDay() === 0;
}

/** The academy is closed on Sundays: a date landing there moves to the next open day. */
export function skipSunday(iso: string): string {
  let out = iso;
  for (let i = 0; i < 7 && isSunday(out); i++) out = shiftIso(out, 1);
  return out;
}

/**
 * Moves `days` and pins the result to an open day **in that direction** — going back
 * from Monday lands on Saturday, going forward from Saturday lands on Monday, so the
 * day arrows never get stuck on the closed Sunday.
 */
export function shiftOpenDay(iso: string, days: number): string {
  let out = shiftIso(iso, days);
  for (let i = 0; i < 7 && isSunday(out); i++) out = shiftIso(out, days < 0 ? -1 : 1);
  return out;
}

/** "SEX" */
export function weekdayShort(iso: string): string {
  const date = isoToDate(iso);
  if (!date) return "";
  return date.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "").toUpperCase();
}

/** "sexta, 13 de setembro" */
export function longDate(iso: string): string {
  const date = isoToDate(iso);
  if (!date) return iso;
  return date.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
}

/** "13/09" */
export function shortDate(iso: string): string {
  const date = isoToDate(iso);
  if (!date) return iso;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

/** "13/09/2026" from a `yyyy-MM-dd` string. */
export function displayDate(iso: string): string {
  const date = isoToDate(iso);
  if (!date) return iso;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** "2h 14min" / "38s" until `iso`; "agora" when in the past. */
export function countdown(iso: string, now: number = Date.now()): string {
  const diff = new Date(iso).getTime() - now;
  if (!Number.isFinite(diff) || diff <= 0) return "agora";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, "0")}min`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** "18:00" from an ISO timestamp. */
export function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
}

/** Timestamp of a persisted attempt, e.g. "13/09 06:00:02". */
export function stampTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TZ,
  });
}
